/**
 * Modificar Incauto / Cargo — migración web de ares8070 (escritorio).
 *
 * Lista SOLO los registros no procesados (incautos o cargos vigentes sin
 * aplicar) y permite corregirles el valor — y en los cargos también la
 * razón — antes de que sigan su curso. Al guardar un cargo, el backend
 * sincroniza prestamo.llamada con el nuevo valor.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin registros pendientes de procesar
      </Typography>
    </Stack>
  );
}

export default function ModificarIncautoCargo() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tipo, setTipo] = useState("incauto");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = async (t = tipo) => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/modajuste_buscar", { tipo: t });
      if (ok) {
        setRows((data.filas || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los registros", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Edición ----
  const [target, setTarget] = useState(null);
  const [valor, setValor] = useState("");
  const [razon, setRazon] = useState("");
  const [guardando, setGuardando] = useState(false);

  const abrirEditar = (row) => {
    setValor(String(row.valor ?? ""));
    setRazon(row.razon || "");
    setTarget(row);
  };

  const guardar = async () => {
    if (toNum(valor) <= 0) {
      showAlert({ message: "El valor no puede estar en 0", color: "warning" });
      return;
    }
    if (tipo === "cargo" && !razon.trim()) {
      showAlert({ message: "La razón del cargo no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/modajuste_guardar", {
        tipo,
        id: target.id,
        valor: toNum(valor),
        razon: razon.trim(),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Registro modificado", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar la modificación", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columnasBase = [
    { field: "noprest", headerName: "Préstamo", width: 100 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "sucursal", headerName: "Sucursal", width: 110 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 180 },
  ];
  const columnasIncauto = [
    ...columnasBase,
    { field: "chasis", headerName: "Chasis", width: 170 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 160,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
  ];
  const columnasCargo = [
    ...columnasBase,
    { field: "razon", headerName: "Razón", flex: 1, minWidth: 160 },
  ];
  const columns = [
    ...(tipo === "cargo" ? columnasCargo : columnasIncauto),
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => abrirEditar(params.row)}
          sx={btnOutline(FP.color.navy)}
        >
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Modificar Incauto / Cargo"
        subtitle="Corrige el valor (y la razón en los cargos) de registros aún no procesados (ares8070)"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setRows([]);
                  cargar(e.target.value);
                }}
                sx={fpInputSx}
              >
                <MenuItem value="incauto">Incautos</MenuItem>
                <MenuItem value="cargo">Cargos</MenuItem>
              </TextField>
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              md={9}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} registros
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Edición de valor / razón */}
      <Dialog
        open={!!target}
        onClose={() => !guardando && setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Modificar {tipo === "cargo" ? "cargo" : "incauto"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            Préstamo <b>{target?.noprest}</b> de <b>{target?.nombres}</b> — valor actual RD${" "}
            <b>{fmtMoney(target?.valor)}</b>.
          </DialogContentText>
          <TextField
            fullWidth
            type="number"
            label="Valor"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            sx={{ ...fpInputSx, mb: tipo === "cargo" ? 2 : 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }}
          />
          {tipo === "cargo" && (
            <TextField
              fullWidth
              label="Razón"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              sx={fpInputSx}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} disabled={guardando} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              guardando ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
            }
            onClick={guardar}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
